import { ArrowBackIcon, ArrowForwardIcon } from '@chakra-ui/icons'
import { Box, Flex, IconButton } from '@chakra-ui/react'
import { useBreakpointContext } from 'context/Breakpoint'
import { useEffect, useState } from 'react'
import { Amplitude } from 'utils/amplitude.utils'
import { TestimonialCarouselContent, Testimony } from './Content/Testimonial'
import useUser from 'hooks/useUser/useUser'

export enum CarouselType {
  TESTIMONIAL = 'testimonial',
  PLAIN = 'plain',
}

interface TestimonialCarouselProps {
  list: unknown[]
  type: CarouselType
}

const handleAmplitudeEvent = (type: CarouselType, variant_label?: string) => {
  switch (type) {
    case CarouselType.TESTIMONIAL:
      Amplitude.checkoutTestimonialClick(variant_label)
      break
    default:
      break
  }
}

export const Carousel = ({ list, type }: TestimonialCarouselProps) => {
  const { isMobile } = useBreakpointContext()
  const { user } = useUser()
  const variantLabel = user?.waitlistUser.variantLabel ?? ''

  // next and prev logic (desktop/buttons)
  const [index, setIndex] = useState(1)
  const [transitionEnabled, setTransitionEnabled] = useState(true)

  const total = list.length
  const extendedList = [list[total - 1], ...list, list[0]]

  const handlePrev = () => {
    handleAmplitudeEvent(type, variantLabel)

    if (index > 0) {
      setIndex((prev) => prev - 1)
    }
  }

  const handleNext = () => {
    handleAmplitudeEvent(type, variantLabel)

    if (index < total + 1) {
      setIndex((prev) => prev + 1)
    }
  }

  const handleTransitionEnd = () => {
    if (index === 0) {
      setTransitionEnabled(false)
      setIndex(total)
    } else if (index === total + 1) {
      setTransitionEnabled(false)
      setIndex(1)
    }
  }

  useEffect(() => {
    if (!transitionEnabled) {
      const timeout = setTimeout(() => setTransitionEnabled(true), 300)
      return () => clearTimeout(timeout)
    }
  }, [transitionEnabled])

  // Swipe logic (mobile)
  const [touchStartX, setTouchStartX] = useState<number | null>(null)
  const [dragOffset, setDragOffset] = useState(0)
  const minSwipeDistance = 50

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartX !== null) {
      const currentX = e.targetTouches[0].clientX
      const offset = currentX - touchStartX
      setDragOffset(offset)
    }
  }

  const onTouchEnd = () => {
    const isLeftSwipe = dragOffset * -1 > minSwipeDistance
    const isRightSwipe = dragOffset * -1 < -minSwipeDistance

    if (isLeftSwipe) {
      handleNext()
    } else if (isRightSwipe) {
      handlePrev()
    }

    setTouchStartX(null)
    setDragOffset(0)
  }

  // Render content based on type
  const renderContent = (item: unknown) => {
    switch (type) {
      case 'testimonial':
        const testimony = item as Testimony
        return (
          <TestimonialCarouselContent
            quote={testimony.quote}
            author={testimony.author}
          />
        )
      default:
        return null
    }
  }

  return (
    <Flex direction="column" align="center" justify="center">
      <Flex align="center" justify="center" overflow="hidden" width="100%">
        {!isMobile && (
          <IconButton
            icon={<ArrowBackIcon boxSize={4} />}
            aria-label="Previous"
            onClick={handlePrev}
            variant="ghost"
            size="sm"
          />
        )}
        <Box
          overflow="hidden"
          w="100%"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          mx={1}
        >
          <Flex
            align="center"
            gap={'5px'}
            transition={
              touchStartX || !transitionEnabled
                ? 'none'
                : 'transform 0.3s ease-in-out'
            }
            transform={`translateX(calc(-${index * 100}% - ${
              index * 5
            }px + ${dragOffset}px))`}
            onTransitionEnd={handleTransitionEnd}
          >
            {extendedList.map((item, i) => (
              <Box
                key={i}
                bg="#F0F6FE"
                p={4}
                borderRadius="md"
                minW="100%"
                textAlign="left"
              >
                {renderContent(item)}
              </Box>
            ))}
          </Flex>
        </Box>
        {!isMobile && (
          <IconButton
            icon={<ArrowForwardIcon boxSize={4} />}
            aria-label="Next"
            onClick={handleNext}
            variant="ghost"
            size="sm"
          />
        )}
      </Flex>
      {isMobile && (
        <Flex mt={4} gap={2}>
          {list.map((_, i) => (
            <Box
              key={i}
              w="10px"
              h="10px"
              borderRadius="full"
              border="2px solid #0B0C35"
              bg={i === (index - 1) % list.length ? '#0B0C35' : 'transparent'}
              transition="background-color 0.3s ease"
            />
          ))}
        </Flex>
      )}
    </Flex>
  )
}
